<template>
  <div class="signin-page">
    <!-- Decorative Background -->
    <div class="signin-bg">
      <div class="bg-gradient" />
      <div class="bg-pattern" />
      <div class="bg-circle bg-circle-1" />
      <div class="bg-circle bg-circle-2" />
    </div>

    <!-- Back to Landing Link -->
    <router-link
      to="/"
      class="back-link"
    >
      <i class="fas fa-arrow-left" />
      Back to Home
    </router-link>

    <div class="signin-container">
      <div class="signin-card">
        <!-- Header -->
        <div class="signin-header">
          <div class="logo-wrapper">
            <img
              src="@/assets/images/logo/osaslogo.png"
              alt="NC OSAS Logo"
              class="signin-logo"
            >
          </div>
          <h1 class="signin-title">
            NC OSAS
          </h1>
          <p class="signin-subtitle">
            Office of Student Affairs and Services
          </p>
        </div>

        <!-- Form -->
        <div class="signin-form">
          <ValidationObserver
            ref="signinForm"
            v-slot="{ handleSubmit }"
          >
            <form
              role="form"
              novalidate
              @submit.prevent="handleSubmit(onSignIn)"
            >
              <p class="account-type-hint">
                Sign in using your student ID number or staff username.
              </p>

              <div class="form-group">
                <label class="form-label">Student ID Number or Username</label>
                <div class="input-wrapper">
                  <i class="fas fa-user input-icon" />
                  <input
                    v-model="credentials.login"
                    :disabled="state.busy"
                    type="text"
                    class="form-input"
                    placeholder="Enter your student ID or username"
                    autocomplete="off"
                  >
                </div>
              </div>

              <div class="form-group">
                <label class="form-label">Password</label>
                <div class="input-wrapper">
                  <i class="fas fa-lock input-icon" />
                  <input
                    v-model="credentials.password"
                    :disabled="state.busy"
                    :type="showPassword ? 'text' : 'password'"
                    class="form-input"
                    placeholder="Enter your password"
                    autocomplete="off"
                  >
                  <button
                    type="button"
                    class="toggle-password"
                    @click="showPassword = !showPassword"
                  >
                    <i :class="['fas', showPassword ? 'fa-eye-slash' : 'fa-eye']" />
                  </button>
                </div>
              </div>

              <button
                type="submit"
                class="signin-btn"
                :disabled="state.busy || !credentials.login || !credentials.password"
              >
                <span v-if="!state.busy">
                  <i class="fas fa-sign-in-alt" />
                  Sign In
                </span>
                <span v-else>
                  <i class="fas fa-spinner fa-spin" />
                  Signing in...
                </span>
              </button>
            </form>
          </ValidationObserver>
        </div>

        <!-- Footer -->
        <div class="signin-footer">
          <p>Northeastern College</p>
          <p class="copyright powered-by">
            <img
              src="@/assets/images/logo/CIT-logo.png"
              alt="CIT-logo"
              class="cit-logo"
            >
            <span>Powered by NC CIT Department</span>
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

import { SSharedAuth } from '@/services/shared'
import { SStudentAuth } from '@/services/student'
import { getHomeRouteByRoleCode } from '@/config/roles'

export default {
  name: 'SharedLogin',

  middleware: ['guest'],

  metaInfo: () => ({
    title: 'Sign In'
  }),

  data: () => ({
    state: {
      busy: false,
      authorizing: false
    },
    credentials: {
      login: '',
      password: ''
    },
    showPassword: false
  }),

  computed: {
    ...mapGetters({
      darkMode: 'settings/darkModeState',
      user: 'auth/user',
      token: 'auth/token'
    }),

    isAuthenticated () {
      return !!this.user && !!this.token
    },

    userDashboard () {
      return getHomeRouteByRoleCode(this.user)
    },

    redirectTarget () {
      const rawTarget = typeof this.$route?.query?.redirect === 'string'
        ? this.$route.query.redirect
        : ''

      // Allow only local in-app paths.
      if (!rawTarget.startsWith('/')) {
        return null
      }

      if (rawTarget.startsWith('/sign-in')) {
        return null
      }

      return rawTarget
    }
  },

  mounted () {
    this.checkAuthAndRedirect()
  },

  methods: {
    async checkAuthAndRedirect () {
      if (!this.token) {
        return
      }

      this.state.authorizing = true

      try {
        if (this.user?.id) {
          if (this.user.must_change_password) {
            this.$router.replace({ name: this.$store.getters['auth/userType'] === 'student' ? 'student.force-password-change' : 'shared.force-password-change' })
          } else {
            if (this.redirectTarget) {
              this.$router.replace(this.redirectTarget)
            } else {
              this.$router.replace({ name: getHomeRouteByRoleCode(this.user) })
            }
          }
          return
        }

        const userType = this.$store.getters['auth/userType']

        if (userType === 'student') {
          const { data: payload } = await SStudentAuth.getCurrentUser()
          const data = payload?.data || payload

          if (!data?.id) {
            throw new Error('Invalid student payload')
          }

          this.$store.dispatch('auth/fetchUserSuccess', {
            id: data.id,
            email: data.email,
            must_change_password: data.must_change_password,
            role: 'student',
            role_name: 'Student',
            name: `${data.first_name} ${data.last_name}`,
            first_name: data.first_name,
            last_name: data.last_name,
            id_number: data.id_number,
            course: data.course,
            year_level: data.year_level,
            contact_number: data.contact_number,
            middle_name: data.middle_name,
            extension_name: data.extension_name
          })

          if (data.must_change_password) {
            this.$bvToast.toast('Please change your default password before using the system.', {
              title: 'Password Update Required',
              variant: 'warning',
              solid: true
            })
            this.$router.replace({ name: 'student.force-password-change' })
          } else {
            this.$router.replace(this.redirectTarget || { name: 'student.dashboard' })
          }
          return
        }

        const { data: payload } = await SSharedAuth.getCurrentUser()
        const data = payload?.data || payload

        if (!data?.id) {
          throw new Error('Invalid shared payload')
        }

        this.$store.dispatch('auth/fetchUserSuccess', {
          id: data.id,
          email: data.email,
          must_change_password: data.must_change_password,
          role_id: data.role?.id,
          role: data.role?.role_code,
          role_name: data.role?.role_name,
          username: data.username,
          name: data.name,
          first_name: data.first_name,
          last_name: data.last_name
        })

        this.$store.dispatch('auth/setUserType', 'shared')

        if (data.must_change_password) {
          this.$bvToast.toast('Please change your default password before using the system.', {
            title: 'Password Update Required',
            variant: 'warning',
            solid: true
          })
          this.$router.replace({ name: 'shared.force-password-change' })
        } else {
          this.$router.replace({
            name: getHomeRouteByRoleCode(data)
          })
        }
      } catch (error) {
        this.$store.dispatch('auth/logout')
      } finally {
        this.state.authorizing = false
      }
    },

    async authorizeAndRedirect (userData) {
      if (userData?.must_change_password) {
        this.$bvToast.toast('Please change your default password before using the system.', {
          title: 'Password Update Required',
          variant: 'warning',
          solid: true
        })
        this.$router.replace({ name: 'shared.force-password-change' })
        return true
      }

      // Verify user has proper role/permissions before redirecting
      const roleCode = userData?.role?.role_code

      if (!roleCode) {
        this.$bvToast.toast('Your account does not have proper permissions.', {
          title: 'Authorization Failed',
          variant: 'danger',
          solid: true
        })
        this.$store.dispatch('auth/logout')
        return false
      }

      const dashboardName = getHomeRouteByRoleCode(roleCode)
      if (dashboardName === 'shared.login') {
        this.$bvToast.toast('Your account role is not mapped to a dashboard.', {
          title: 'Authorization Failed',
          variant: 'danger',
          solid: true
        })
        this.$store.dispatch('auth/logout')
        return false
      }

      // Check if mapped dashboard route exists
      const routeExists = this.$router.options.routes.some(
        route => route.name === dashboardName ||
        (route.children && route.children.some(child => child.name === dashboardName))
      )

      if (!routeExists) {
        this.$bvToast.toast('Dashboard route is missing for your account role.', {
          title: 'Authorization Failed',
          variant: 'danger',
          solid: true
        })
        this.$store.dispatch('auth/logout')
        return false
      }

      this.$router.replace({ name: dashboardName })
      return true
    },

    async saveAndGetCurrentStudent (token) {
      this.$store.dispatch('auth/saveToken', token)
      this.$store.dispatch('auth/setUserType', 'student')

      return new Promise((resolve, reject) => {
        SStudentAuth.getCurrentUser().then(
          ({ data: payload }) => {
            const profile = payload?.data || payload

            if (!profile?.id) {
              this.$store.dispatch('auth/logout')
              reject(new Error('Invalid student payload'))
              return
            }

            this.$store.dispatch('auth/fetchUserSuccess', {
              id: profile.id,
              email: profile.email,
              must_change_password: profile.must_change_password,
              role: 'student',
              role_name: 'Student',
              name: `${profile.first_name} ${profile.last_name}`,
              first_name: profile.first_name,
              last_name: profile.last_name,
              middle_name: profile.middle_name,
              extension_name: profile.extension_name,
              id_number: profile.id_number,
              course: profile.course,
              year_level: profile.year_level,
              contact_number: profile.contact_number
            })

            if (profile.must_change_password) {
              this.$bvToast.toast('Please change your default password before using the system.', {
                title: 'Password Update Required',
                variant: 'warning',
                solid: true
              })
              this.$router.replace({ name: 'student.force-password-change' })
            } else {
              this.$router.replace(this.redirectTarget || { name: 'student.dashboard' })
            }

            resolve(profile)
          }
        ).catch(error => {
          this.$store.dispatch('auth/logout')
          this.$bvToast.toast('Authentication failed. Please try again.', {
            title: 'Error',
            variant: 'danger',
            solid: true
          })
          reject(error)
        })
      })
    },

    async saveAndGetCurrentUser (token) {
      this.$store.dispatch('auth/saveToken', token)
      this.$store.dispatch('auth/setUserType', 'shared')

      return new Promise((resolve, reject) => {
        SSharedAuth.getCurrentUser().then(
          ({ data: payload }) => {
            const data = payload?.data || payload

            if (!data?.id) {
              this.$store.dispatch('auth/logout')
              reject(new Error('Invalid user payload'))
              return
            }

            this.$store.dispatch('auth/fetchUserSuccess', {
              id: data.id,
              email: data.email,
              must_change_password: data.must_change_password,
              role_id: data.role?.id,
              role: data.role?.role_code,
              role_name: data.role?.role_name,
              username: data.username,
              name: data.name,
              first_name: data.first_name,
              last_name: data.last_name
            })

            // Use the authorization method to properly check permissions
            this.authorizeAndRedirect(data).then(() => {
              resolve(data)
            })
          }
        ).catch(error => {
          this.$store.dispatch('auth/logout')
          this.$bvToast.toast('Authentication failed. Please try again.', {
            title: 'Error',
            variant: 'danger',
            solid: true
          })
          reject(error)
        })
      })
    },

    async onSignIn () {
      await this.$refs.signinForm.validate().then(
        allFieldsAreValid => {
          if (allFieldsAreValid) {
            this.state.busy = true
            SSharedAuth.signInUnified(this.credentials).then(
              ({ data }) => {
                if (!data?.success || !data?.token || !data?.user_type) {
                  this.$bvToast.toast(data?.message || 'Unable to sign in. Please check your credentials.', {
                    title: 'Authentication Failed',
                    variant: 'danger',
                    solid: true
                  })
                  return
                }

                if (data.user_type === 'student') {
                  this.saveAndGetCurrentStudent(data.token).finally(() => {
                    this.state.busy = false
                  })
                  return
                }

                this.saveAndGetCurrentUser(data.token).finally(() => {
                  this.state.busy = false
                })
              }
            ).catch(() => {
              this.$bvToast.toast('Invalid credentials. Please try again.', {
                title: 'Authentication Failed',
                variant: 'danger',
                solid: true
              })
              this.state.busy = false
            })
          }
        }
      )
    },

    darkChange (mode) {
      this.modeChange({ rtl: false, dark: mode })
    },

    ...mapActions({
      modeChange: 'settings/layoutModeAction'
    })
  }
}
</script>

<style scoped>
.signin-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  padding: 1rem;
}

/* Background */
.signin-bg {
  position: absolute;
  inset: 0;
  z-index: 0;
}

.bg-gradient {
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, #059669 0%, #10b981 50%, #34d399 100%);
}

.bg-pattern {
  position: absolute;
  inset: 0;
  background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
}

.bg-circle {
  position: absolute;
  border-radius: 50%;
  background: rgba(251, 191, 36, 0.2);
}

.bg-circle-1 {
  width: 400px;
  height: 400px;
  top: -100px;
  right: -100px;
}

.bg-circle-2 {
  width: 300px;
  height: 300px;
  bottom: -50px;
  left: -50px;
  background: rgba(255, 255, 255, 0.1);
}

/* Back Link */
.back-link {
  position: absolute;
  top: 1.5rem;
  left: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: white;
  text-decoration: none;
  font-weight: 500;
  font-size: 0.875rem;
  padding: 0.5rem 1rem;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 50px;
  backdrop-filter: blur(4px);
  transition: all 0.3s;
  z-index: 10;
}

.back-link:hover {
  background: rgba(255, 255, 255, 0.25);
  transform: translateX(-4px);
  color: white;
  text-decoration: none;
}

/* Container */
.signin-container {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 400px;
}

/* Card */
.signin-card {
  background: white;
  border-radius: 24px;
  padding: 2rem;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
}

/* Header */
.signin-header {
  text-align: center;
  margin-bottom: 2rem;
}

.logo-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 1rem;
}

.signin-logo {
  width: 84px;
  height: 84px;
  object-fit: contain;
}

.signin-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: #1e3a2f;
  margin: 0;
}

.signin-subtitle {
  color: #6b7280;
  font-size: 0.875rem;
  margin: 0.25rem 0 0;
}

/* Form */
.signin-form {
  margin-bottom: 1.5rem;
}

.account-type-switch {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem;
}

.type-btn {
  border: 1px solid #d1d5db;
  background: #f9fafb;
  color: #374151;
  border-radius: 10px;
  padding: 0.625rem 0.75rem;
  font-size: 0.875rem;
  font-weight: 600;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s;
}

.type-btn:hover:not(:disabled) {
  border-color: #10b981;
  color: #065f46;
}

.type-btn.active {
  border-color: #10b981;
  background: rgba(16, 185, 129, 0.12);
  color: #065f46;
}

.type-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.account-type-hint {
  font-size: 0.8125rem;
  color: #6b7280;
  margin-bottom: 1rem;
}

.form-group {
  margin-bottom: 1.25rem;
}

.form-label {
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.5rem;
}

.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.input-icon {
  position: absolute;
  left: 1rem;
  color: #9ca3af;
  font-size: 0.875rem;
  pointer-events: none;
}

.form-input {
  width: 100%;
  padding: 0.875rem 1rem 0.875rem 2.75rem;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  font-size: 0.875rem;
  transition: all 0.2s;
  outline: none;
}

.form-input:focus {
  border-color: #10b981;
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
}

.form-input:disabled {
  background: #f9fafb;
  cursor: not-allowed;
}

.toggle-password {
  position: absolute;
  right: 1rem;
  background: none;
  border: none;
  color: #9ca3af;
  cursor: pointer;
  padding: 0.25rem;
}

.toggle-password:hover {
  color: #6b7280;
}

/* Submit Button */
.signin-btn {
  width: 100%;
  padding: 0.875rem;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transition: all 0.3s;
  box-shadow: 0 4px 15px rgba(16, 185, 129, 0.3);
}

.signin-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(16, 185, 129, 0.4);
}

.signin-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

/* Footer */
.signin-footer {
  text-align: center;
  padding-top: 1rem;
  border-top: 1px solid #e5e7eb;
}

.signin-footer p {
  margin: 0;
  color: #6b7280;
  font-size: 0.75rem;
}

.signin-footer .copyright {
  margin-top: 0.25rem;
  color: #9ca3af;
}

.signin-footer .powered-by {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
}

.signin-footer .cit-logo {
  width: 18px;
  height: 18px;
  object-fit: contain;
}

/* Responsive */
@media (min-width: 640px) {
  .signin-card {
    padding: 2.5rem;
  }
}
</style>
